import React from "react";
import "./Sidebar.css";

/**
 * Left-rail navigation chrome. Shared by the dashboard sidebar and (PRD 2) the
 * per-game manager. Surface-aware.
 *
 *   <Sidebar>
 *     <SidebarSection label="Library">
 *       <SidebarItem label="All Games" count={8} active onClick={…} />
 *       <SidebarItem label="Drafts" count={3} dot="var(--color-orange)" />
 *     </SidebarSection>
 *   </Sidebar>
 */
export const SidebarSection = ({ label, children, className = "" }) => (
  <div className={`gx-sb-section ${className}`}>
    {label && <div className="gx-sb-label">{label}</div>}
    {children}
  </div>
);

export const SidebarItem = ({
  icon,
  label,
  count,
  trailingLabel,
  active = false,
  dot,
  disabled = false,
  as: Tag = "button",
  className = "",
  children,
  ...rest
}) => (
  <Tag
    className={`gx-sb-item ${active ? "is-active" : ""} ${disabled ? "is-disabled" : ""} ${className}`}
    aria-current={active ? "page" : undefined}
    disabled={Tag === "button" ? disabled : undefined}
    {...rest}
  >
    {dot ? (
      <span className="gx-sb-dot" style={{ background: dot }} />
    ) : (
      <span className="gx-sb-icon">{icon}</span>
    )}
    <span className="gx-sb-text">{label || children}</span>
    {count != null && <span className="gx-sb-count">{count}</span>}
    {trailingLabel != null && (
      <span className="gx-sb-trailing">{trailingLabel}</span>
    )}
  </Tag>
);

/**
 * Sidebar.
 *
 * Default: a static left rail. Pass `drawer` for an off-canvas slide-in/out
 * (mobile, or a collapsible desktop rail): the rail becomes fixed-position and
 * slides based on `open`, with a click-to-dismiss overlay. `onClose` fires when
 * the overlay is clicked.
 *
 * Props: width, drawer (bool), open (bool, default true), onClose.
 */
const Sidebar = ({
  width = 224,
  drawer = false,
  open = true,
  onClose,
  className = "",
  style,
  children,
  ...rest
}) => {
  const rail = (
    <aside
      className={`gx-sidebar ${drawer ? "gx-sidebar--drawer" : ""} ${drawer && open ? "is-open" : ""} ${className}`}
      style={{ width, ...style }}
      aria-hidden={drawer && !open ? true : undefined}
      {...rest}
    >
      {children}
    </aside>
  );

  if (!drawer) return rail;

  return (
    <>
      <div
        className={`gx-sidebar-overlay ${open ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      {rail}
    </>
  );
};

export default Sidebar;
